import React, { Suspense, lazy, useState, useEffect } from "react";
import axios from "axios";
import { createContext, useContext } from "react";
import { toast } from "react-toastify";
import { councilSlugFromHost, openCouncilApp, rootUrl } from "../helpers";

const LoginSignupModal = lazy(() =>
  import(/* webpackChunkName: "auth-login" */ "../shared/components/auth/loginModal"),
);

const PlatformLogin = lazy(() =>
  import(/* webpackChunkName: "platform-login" */ "../pages/platform/PlatformLogin"),
);

const AuthContext = createContext({ user: null, login: null, logOut: null });

function applyStaffHandoffFromQuery() {
  try {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("staff_token");
    if (!token) return false;
    const uid = params.get("staff_uid");
    let user = null;
    const rawUser = params.get("staff_user");
    if (rawUser) {
      try {
        user = JSON.parse(decodeURIComponent(escape(atob(rawUser))));
      } catch (e) {
        user = null;
      }
    }
    if (!user && uid) {
      user = { id: Number(uid), act_as: true };
    }
    if (user?.id) {
      localStorage.setItem("userId", String(user.id));
      localStorage.setItem(String(user.id), JSON.stringify(user));
    }
    localStorage.setItem("apiToken", token);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    axios.defaults.headers.common["X-Auth-Token"] = token;
    params.delete("staff_token");
    params.delete("staff_uid");
    params.delete("staff_user");
    const next = `${window.location.pathname}${params.toString() ? `?${params}` : ""}${window.location.hash || ""}`;
    window.history.replaceState({}, "", next);
    return true;
  } catch (e) {
    return false;
  }
}

export function AuthProvider({ children }) {
  applyStaffHandoffFromQuery();

  const clearSession = () => {
    localStorage.clear();
    delete axios.defaults.headers.common.Authorization;
    delete axios.defaults.headers.common["X-Auth-Token"];
  };

  const getUser = () => {
    const personId = localStorage.getItem("userId");
    try {
      return JSON.parse(localStorage.getItem(personId));
    } catch (error) {
      return null;
    }
  };
  const savedToken = localStorage.getItem("apiToken");
  if (savedToken) {
    axios.defaults.headers.common.Authorization = `Bearer ${savedToken}`;
    axios.defaults.headers.common["X-Auth-Token"] = savedToken;
  }
  const [user, setUser] = useState(getUser);

  useEffect(() => {
    const requestInterceptorId = axios.interceptors.request.use((config) => {
      const token = localStorage.getItem("apiToken");
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
        config.headers["X-Auth-Token"] = token;
      }
      return config;
    });

    let hasHandledUnauthorized = false;
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        if (status === 401 && !hasHandledUnauthorized) {
          hasHandledUnauthorized = true;
          clearSession();
          setUser(null);
          toast.error("Session expired. Please login again.");
          setTimeout(() => {
            window.location.reload();
          }, 100);
        }
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptorId);
      axios.interceptors.response.eject(interceptorId);
    };
  }, []);

  const login = async (values, callback) => {
    try {
      const response = await axios.post(rootUrl("/login"), values);
      if (response.data.status === 200) {
        const authData = response.data.data || {};
        const fetchedUser = {
          ...(authData.user || authData),
          memberships: authData.memberships || [],
          platform: !!authData.platform,
          is_platform_admin: authData.platform || authData.user?.is_platform_admin ? 1 : 0,
          root_domain: authData.root_domain,
        };
        const token = authData.token || "";
        const userId = fetchedUser?.id || fetchedUser?.userId;
        const onCouncilHost = !!councilSlugFromHost();

        if (!onCouncilHost && !fetchedUser.platform && (fetchedUser.memberships || []).length === 1) {
          const only = fetchedUser.memberships[0];
          if (only?.slug) {
            toast.success("Login Successful");
            openCouncilApp(only.slug, { token, userId, user: fetchedUser });
            return;
          }
        }

        if (userId) {
          localStorage.setItem("userId", userId);
          localStorage.setItem(String(userId), JSON.stringify(fetchedUser));
        }
        if (token) {
          localStorage.setItem("apiToken", token);
          axios.defaults.headers.common.Authorization = `Bearer ${token}`;
          axios.defaults.headers.common["X-Auth-Token"] = token;
        }
        setUser(fetchedUser);
        toast.success("Login Successful");
        callback();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "An error occured while logging in");
    }
  };

  const logOut = (callback) => {
    clearSession();
    callback();
  };

  const value = { user, login, logOut };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function RequireAuth({ children, platform = false }) {
  const auth = useAuth();
  const usePlatformLogin = platform && !councilSlugFromHost();
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (!auth?.user && !usePlatformLogin) setIsModalOpen(true);
  }, [auth?.user, usePlatformLogin]);

  if (!auth?.user && usePlatformLogin) {
    return (
      <Suspense fallback={null}>
        <PlatformLogin />
      </Suspense>
    );
  }

  return (
    <>
      {isModalOpen && !auth?.user ? (
        <Suspense fallback={null}>
          <LoginSignupModal open handleClose={() => setIsModalOpen(false)} />
        </Suspense>
      ) : null}
      {children}
    </>
  );
}

export function useAuth() {
  const auth = useContext(AuthContext);
  if (!auth) {
    throw new Error("Auth context is null");
  }
  return auth;
}
